import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import QRCode from 'qrcode';
import type { WebEvent } from '../types/events';
import type { WebMedia } from '../types/media';
import {
  buildDefaultBlastMessage,
  buildDefaultEventDateTimeLabel,
  buildDefaultLocationLabel,
  bulkCreateGuestInvites,
  clearEventCoverMedia,
  deleteEventMediaBatch,
  formatManagementCount,
  formatManagementStorage,
  loadEventAdminShareLink,
  loadEventBlastManagement,
  loadEventReactionCount,
  loadManagementUsage,
  parseBulkInviteText,
  processEventBlastMessageNow,
  saveEventBlastTemplate,
  scheduleEventBlastMessage,
  setEventCoverMedia,
  updateEventLocation,
  type EventBlastAudienceType,
  type EventBlastMessageType,
  type WebEventBlastTemplate,
  type WebManagementUsage,
  type WebScheduledEventMessage,
} from '../services/eventManagement';
import type { WebGuestInvite } from '../types/people';
import './event-management.css';

type Props = {
  event: WebEvent;
  currentUserId: string;
  media: WebMedia[];
  onEventUpdated: (event: WebEvent) => void;
  onGalleryChanged: () => void;
};

const THEME_COLORS = ['#2563EB', '#4F46E5', '#0F766E', '#BE123C', '#7C3AED', '#EA580C'];

function dateTimeLocalValue(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatScheduledDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function mediaThumb(media: WebMedia): string | null {
  return media.media_type === 'video'
    ? media.delivery?.poster ?? null
    : media.delivery?.thumb ?? media.delivery?.display ?? null;
}

function ratioLabel(used: unknown, limit: unknown): string {
  const safeUsed = Math.max(0, Number(used ?? 0) || 0);
  const safeLimit = Math.max(0, Number(limit ?? 0) || 0);
  return safeLimit > 0
    ? `${Math.round(safeUsed).toLocaleString()} / ${Math.round(safeLimit).toLocaleString()}`
    : Math.round(safeUsed).toLocaleString();
}

function addDays(base: Date, days: number): Date {
  const next = new Date(base);
  next.setDate(next.getDate() + days);
  return next;
}

function subtractDays(base: Date, days: number): Date {
  const next = new Date(base);
  next.setDate(next.getDate() - days);
  return next;
}

export function EventManagementPanel({
  event,
  currentUserId,
  media,
  onEventUpdated,
  onGalleryChanged,
}: Props) {
  const [usage, setUsage] = useState<WebManagementUsage | null>(null);
  const [reactionCount, setReactionCount] = useState(0);
  const [shareLink, setShareLink] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [locationName, setLocationName] = useState(event.location_name ?? '');
  const [locationAddress, setLocationAddress] = useState(event.location_address ?? '');
  const [bulkText, setBulkText] = useState('');
  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const [template, setTemplate] = useState<WebEventBlastTemplate | null>(null);
  const [invites, setInvites] = useState<WebGuestInvite[]>([]);
  const [messages, setMessages] = useState<WebScheduledEventMessage[]>([]);
  const [blastTitle, setBlastTitle] = useState(event.name || 'Save the Date');
  const [blastDateLabel, setBlastDateLabel] = useState(buildDefaultEventDateTimeLabel(event));
  const [blastLocationLabel, setBlastLocationLabel] = useState(buildDefaultLocationLabel(event));
  const [blastMessage, setBlastMessage] = useState(buildDefaultBlastMessage(event.name));
  const [themeColor, setThemeColor] = useState(THEME_COLORS[0]);
  const [includeQrCode, setIncludeQrCode] = useState(true);
  const [includeEventCode, setIncludeEventCode] = useState(true);
  const [includeJoinButton, setIncludeJoinButton] = useState(true);
  const [audienceType, setAudienceType] = useState<EventBlastAudienceType>('all_invited');
  const [selectedInviteIds, setSelectedInviteIds] = useState<string[]>([]);
  const [messageType, setMessageType] = useState<EventBlastMessageType>('save_the_date');
  const [scheduledFor, setScheduledFor] = useState(dateTimeLocalValue(addDays(new Date(), 1)));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const coverCandidates = useMemo(
    () => media.filter((item) => item.media_type !== 'video' && String(item.moderation_status ?? '').toLowerCase() === 'approved').slice(0, 24),
    [media],
  );

  const bulkCandidates = useMemo(() => parseBulkInviteText(bulkText), [bulkText]);

  async function refreshManagement() {
    setError(null);
    try {
      const [nextUsage, nextReactionCount, nextShareLink, blast] = await Promise.all([
        loadManagementUsage(event.id).catch((err) => {
          console.warn('[web management] usage unavailable:', err);
          return null;
        }),
        loadEventReactionCount(event.id),
        loadEventAdminShareLink(event),
        loadEventBlastManagement(event.id).catch((err) => {
          console.warn('[web management] blast management unavailable:', err);
          return { template: null, invites: [], messages: [] };
        }),
      ]);
      setUsage(nextUsage);
      setReactionCount(nextReactionCount);
      setShareLink(nextShareLink);
      setTemplate(blast.template);
      setInvites(blast.invites);
      setMessages(blast.messages);
      if (blast.template) {
        setBlastTitle(blast.template.title);
        setBlastDateLabel(blast.template.event_datetime_label ?? buildDefaultEventDateTimeLabel(event));
        setBlastLocationLabel(blast.template.location_label ?? buildDefaultLocationLabel(event));
        setBlastMessage(blast.template.custom_message ?? buildDefaultBlastMessage(event.name));
        setThemeColor(blast.template.theme_color || THEME_COLORS[0]);
        setIncludeQrCode(blast.template.include_qr_code);
        setIncludeEventCode(blast.template.include_event_code);
        setIncludeJoinButton(blast.template.include_join_button);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load event management.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    setLocationName(event.location_name ?? '');
    setLocationAddress(event.location_address ?? '');
    setBlastTitle(event.name || 'Save the Date');
    setBlastDateLabel(buildDefaultEventDateTimeLabel(event));
    setBlastLocationLabel(buildDefaultLocationLabel(event));
    setBlastMessage(buildDefaultBlastMessage(event.name));
    void refreshManagement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  useEffect(() => {
    let active = true;
    if (!shareLink) {
      setQrDataUrl('');
      return () => { active = false; };
    }
    QRCode.toDataURL(shareLink, { width: 240, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => { if (active) setQrDataUrl(url); })
      .catch((err) => console.warn('[web management] QR generation failed:', err));
    return () => { active = false; };
  }, [shareLink]);

  async function copyShareLink() {
    if (!shareLink || !navigator.clipboard) return;
    await navigator.clipboard.writeText(shareLink);
    setNotice('Event link copied.');
  }

  async function shareEvent() {
    if (!shareLink) return;
    const text = `${event.name}\nEvent code: ${event.code || ''}\n${shareLink}`;
    if (navigator.share) {
      await navigator.share({ title: event.name, text, url: shareLink });
      return;
    }
    await copyShareLink();
  }

  async function chooseCover(mediaItem: WebMedia) {
    setBusy(`cover:${mediaItem.id}`);
    setError(null);
    setNotice(null);
    try {
      const result = await setEventCoverMedia(event.id, mediaItem);
      onEventUpdated({ ...event, cover_photo_url: result.coverPhotoUrl });
      setNotice('Event cover updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update the event cover.');
    } finally {
      setBusy(null);
    }
  }

  async function resetCover() {
    setBusy('cover:auto');
    setError(null);
    setNotice(null);
    try {
      const result = await clearEventCoverMedia(event.id);
      onEventUpdated({ ...event, cover_photo_url: result.coverPhotoUrl });
      setNotice('Event cover returned to automatic selection.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset the event cover.');
    } finally {
      setBusy(null);
    }
  }

  async function saveLocation() {
    setBusy('location');
    setError(null);
    setNotice(null);
    try {
      const patch = await updateEventLocation(event.id, locationName, locationAddress);
      onEventUpdated({ ...event, ...patch });
      setBlastLocationLabel([patch.location_name, patch.location_address].filter(Boolean).join(' · '));
      setNotice('Event location saved. Existing map coordinates are preserved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save event location.');
    } finally {
      setBusy(null);
    }
  }

  async function loadBulkFile(change: ChangeEvent<HTMLInputElement>) {
    const file = change.currentTarget.files?.[0];
    change.currentTarget.value = '';
    if (!file) return;
    const text = await file.text();
    setBulkText(text);
  }

  async function importBulkInvites() {
    if (bulkCandidates.length === 0) return;
    setBusy('bulk-invites');
    setError(null);
    setNotice(null);
    try {
      const result = await bulkCreateGuestInvites({ event, inviterUserId: currentUserId, candidates: bulkCandidates });
      setNotice(`Guest import complete: ${result.created} invite${result.created === 1 ? '' : 's'} created${result.failed.length ? `, ${result.failed.length} failed` : ''}.`);
      if (result.failed.length === 0) setBulkText('');
      await refreshManagement();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to import guest invites.');
    } finally {
      setBusy(null);
    }
  }

  function toggleMedia(id: string) {
    setSelectedMediaIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  async function deleteSelectedMedia() {
    if (selectedMediaIds.length === 0) return;
    if (!window.confirm(`Remove ${selectedMediaIds.length} selected media item${selectedMediaIds.length === 1 ? '' : 's'} from this event?`)) return;
    setBusy('delete-media');
    setError(null);
    setNotice(null);
    try {
      const result = await deleteEventMediaBatch(event.id, selectedMediaIds);
      setSelectedMediaIds((current) => current.filter((id) => !result.deletedIds.includes(id)));
      setNotice(`${result.deletedIds.length} media item${result.deletedIds.length === 1 ? '' : 's'} removed${result.failed.length ? `; ${result.failed.length} could not be removed` : ''}.`);
      onGalleryChanged();
      await refreshManagement();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to remove selected media.');
    } finally {
      setBusy(null);
    }
  }

  async function saveBlast(): Promise<WebEventBlastTemplate | null> {
    setBusy('blast-save');
    setError(null);
    setNotice(null);
    try {
      const saved = await saveEventBlastTemplate({
        templateId: template?.id ?? null,
        eventId: event.id,
        hostUserId: currentUserId,
        title: blastTitle,
        eventDateTimeLabel: blastDateLabel,
        locationLabel: blastLocationLabel,
        coverPhotoUrl: event.cover_photo_url,
        customMessage: blastMessage,
        themeColor,
        includeQrCode,
        includeEventCode,
        includeJoinButton,
      });
      setTemplate(saved);
      setNotice('Save-the-Date design saved.');
      return saved;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save the Save-the-Date design.');
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function scheduleBlast(kind: 'now' | 'custom' | '1w' | '3d' | '1d') {
    if (audienceType === 'selected_invites' && selectedInviteIds.length === 0) {
      setError('Choose at least one invited guest for the selected-guests audience.');
      return;
    }

    let date = new Date();
    if (kind === 'custom') date = new Date(scheduledFor);
    if (kind !== 'now' && kind !== 'custom') {
      const eventStart = new Date(event.start_time || event.event_date || '');
      if (Number.isNaN(eventStart.getTime())) {
        setError('Add an event start date before scheduling automatic reminders.');
        return;
      }
      date = subtractDays(eventStart, kind === '1w' ? 7 : kind === '3d' ? 3 : 1);
      if (date < new Date()) date = new Date();
    }
    if (Number.isNaN(date.getTime())) {
      setError('Choose a valid reminder date and time.');
      return;
    }

    setBusy('blast-send');
    setError(null);
    setNotice(null);
    try {
      const saved = await saveEventBlastTemplate({
        templateId: template?.id ?? null,
        eventId: event.id,
        hostUserId: currentUserId,
        title: blastTitle,
        eventDateTimeLabel: blastDateLabel,
        locationLabel: blastLocationLabel,
        coverPhotoUrl: event.cover_photo_url,
        customMessage: blastMessage,
        themeColor,
        includeQrCode,
        includeEventCode,
        includeJoinButton,
      });
      setTemplate(saved);
      const scheduled = await scheduleEventBlastMessage({
        event,
        template: saved,
        createdBy: currentUserId,
        messageType: kind === 'now' ? messageType : kind === 'custom' ? messageType : 'event_reminder',
        scheduledFor: date,
        audienceType,
        selectedInviteIds,
      });
      if (kind === 'now') await processEventBlastMessageNow(scheduled.id, event.id);
      setNotice(kind === 'now' ? 'Reminder sent to eligible Gathrd users.' : `Reminder scheduled for ${formatScheduledDate(scheduled.scheduled_for)}.`);
      await refreshManagement();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to schedule this event reminder.');
    } finally {
      setBusy(null);
    }
  }

  function toggleInvite(id: string) {
    setSelectedInviteIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  if (loading) return <div className="management-loading">Loading event management…</div>;

  return (
    <section className="management-center" aria-live="polite">
      <div className="management-heading">
        <div>
          <p className="eyebrow">Management Center</p>
          <h2>Run the event from the web</h2>
          <p>These controls use the same event, invite, media, and notification records as the mobile app.</p>
        </div>
        <button className="legacy-btn secondary" type="button" onClick={() => void refreshManagement()} disabled={busy !== null}>Refresh</button>
      </div>

      {error && <p className="error-banner">{error}</p>}
      {notice && <p className="management-notice">{notice}</p>}

      <div className="management-stat-grid">
        <article><strong>{ratioLabel(usage?.guest_count, usage?.guest_limit)}</strong><span>Guests / limit</span></article>
        <article><strong>{ratioLabel(usage?.photo_count, usage?.photo_limit)}</strong><span>Photos / limit</span></article>
        <article><strong>{ratioLabel(usage?.host_video_count, usage?.effective_host_video_limit)}</strong><span>Host videos / limit</span></article>
        <article><strong>{formatManagementCount(reactionCount)}</strong><span>Reactions</span></article>
        <article><strong>{formatManagementStorage(usage?.storage_bytes)}</strong><span>Event storage</span></article>
        <article><strong>{usage?.package_display_name || 'Current package'}</strong><span>Event package</span></article>
      </div>

      <div className="management-grid">
        <article className="management-card management-share-card">
          <div className="management-card-heading"><div><h3>Event QR & link</h3><p>Share the same event identity used by mobile.</p></div></div>
          <div className="management-share-layout">
            <div className="management-qr">{qrDataUrl ? <img src={qrDataUrl} alt={`QR code for ${event.name}`} /> : <span>QR unavailable</span>}</div>
            <div className="management-share-copy">
              <label>Event code<input value={event.code || ''} readOnly /></label>
              <label>Share link<input value={shareLink} readOnly /></label>
              <div className="management-actions">
                <button className="legacy-btn secondary" type="button" onClick={() => void copyShareLink()}>Copy link</button>
                <button className="legacy-btn primary" type="button" onClick={() => void shareEvent()}>Share event</button>
              </div>
            </div>
          </div>
        </article>

        <article className="management-card">
          <div className="management-card-heading"><div><h3>Location</h3><p>Edit the venue text shown across Gathrd.</p></div></div>
          <label>Location name<input value={locationName} onChange={(e) => setLocationName(e.target.value)} placeholder="Venue or place" /></label>
          <label>Address<input value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)} placeholder="Street, city, state" /></label>
          <p className="management-footnote">Web edits preserve the existing mobile map coordinates; browser geocoding is not introduced here.</p>
          <button className="legacy-btn primary" type="button" onClick={() => void saveLocation()} disabled={busy !== null}>{busy === 'location' ? 'Saving…' : 'Save location'}</button>
        </article>

        <article className="management-card management-cover-card">
          <div className="management-card-heading">
            <div><h3>Event photo</h3><p>Choose an approved event photo or return to automatic selection.</p></div>
            <button className="legacy-btn secondary" type="button" onClick={() => void resetCover()} disabled={busy !== null}>Automatic</button>
          </div>
          {coverCandidates.length === 0 ? (
            <p className="management-empty">No approved photos are available as cover candidates yet.</p>
          ) : (
            <div className="management-cover-grid">
              {coverCandidates.map((item) => {
                const url = mediaThumb(item);
                return <button key={item.id} type="button" className="management-cover-option" onClick={() => void chooseCover(item)} disabled={busy !== null}>{url ? <img src={url} alt="Cover candidate" loading="lazy" /> : <span>Photo</span>}</button>;
              })}
            </div>
          )}
        </article>

        <article className="management-card">
          <div className="management-card-heading"><div><h3>Bulk guest invites</h3><p>Paste or upload CSV rows containing an email address and optional name.</p></div></div>
          <textarea rows={7} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={'Name,email@example.com\nAnother Guest,guest@example.com'} />
          <div className="management-actions">
            <label className="legacy-btn secondary file-button">Load CSV<input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void loadBulkFile(e)} /></label>
            <button className="legacy-btn primary" type="button" onClick={() => void importBulkInvites()} disabled={busy !== null || bulkCandidates.length === 0}>{busy === 'bulk-invites' ? 'Importing…' : `Create ${bulkCandidates.length} invite${bulkCandidates.length === 1 ? '' : 's'}`}</button>
          </div>
          <p className="management-footnote">This creates Gathrd invite records. It does not claim an external email was delivered.</p>
        </article>
      </div>

      <article className="management-card management-blast-card">
        <div className="management-card-heading"><div><h3>Save-the-Date & reminders</h3><p>Notification-only reminders match the current mobile feature: in-app plus push for eligible Gathrd users.</p></div></div>
        <div className="management-form-grid">
          <label>Title<input value={blastTitle} onChange={(e) => setBlastTitle(e.target.value)} /></label>
          <label>Date/time label<input value={blastDateLabel} onChange={(e) => setBlastDateLabel(e.target.value)} /></label>
          <label>Location label<input value={blastLocationLabel} onChange={(e) => setBlastLocationLabel(e.target.value)} /></label>
          <label>Audience<select value={audienceType} onChange={(e) => setAudienceType(e.target.value as EventBlastAudienceType)}><option value="all_invited">All invited guests</option><option value="not_joined">Invited guests who have not joined</option><option value="not_uploaded">Joined guests who have not uploaded</option><option value="all_members">All joined guests</option><option value="selected_invites">Selected invited guests</option></select></label>
          <label>Message type<select value={messageType} onChange={(e) => setMessageType(e.target.value as EventBlastMessageType)}><option value="save_the_date">Save the date</option><option value="event_reminder">Event reminder</option><option value="join_reminder">Join reminder</option><option value="upload_reminder">Upload reminder</option></select></label>
          <label>Custom schedule<input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} /></label>
        </div>
        <label>Message<textarea rows={4} value={blastMessage} onChange={(e) => setBlastMessage(e.target.value)} /></label>
        <div className="management-theme-row"><span>Theme</span>{THEME_COLORS.map((color) => <button key={color} type="button" aria-label={`Use ${color} theme`} className={themeColor === color ? 'selected' : ''} style={{ backgroundColor: color }} onClick={() => setThemeColor(color)} />)}</div>
        <div className="management-toggle-row">
          <label><input type="checkbox" checked={includeQrCode} onChange={(e) => setIncludeQrCode(e.target.checked)} /> Include QR</label>
          <label><input type="checkbox" checked={includeEventCode} onChange={(e) => setIncludeEventCode(e.target.checked)} /> Event code</label>
          <label><input type="checkbox" checked={includeJoinButton} onChange={(e) => setIncludeJoinButton(e.target.checked)} /> Join button</label>
        </div>
        {audienceType === 'selected_invites' && <div className="management-invite-picker">{invites.map((invite) => <label key={invite.id}><input type="checkbox" checked={selectedInviteIds.includes(invite.id)} onChange={() => toggleInvite(invite.id)} /><span>{invite.full_name || invite.email || invite.phone || invite.invite_code || 'Invite'}</span></label>)}</div>}
        <div className="management-actions management-blast-actions">
          <button className="legacy-btn secondary" type="button" onClick={() => void saveBlast()} disabled={busy !== null}>{busy === 'blast-save' ? 'Saving…' : 'Save design'}</button>
          <button className="legacy-btn primary" type="button" onClick={() => void scheduleBlast('now')} disabled={busy !== null}>Send now</button>
          <button className="legacy-btn secondary" type="button" onClick={() => void scheduleBlast('custom')} disabled={busy !== null}>Schedule custom</button>
          <button className="legacy-btn secondary" type="button" onClick={() => void scheduleBlast('1w')} disabled={busy !== null}>1 week before</button>
          <button className="legacy-btn secondary" type="button" onClick={() => void scheduleBlast('3d')} disabled={busy !== null}>3 days before</button>
          <button className="legacy-btn secondary" type="button" onClick={() => void scheduleBlast('1d')} disabled={busy !== null}>1 day before</button>
        </div>
        <div className="management-scheduled-list">
          <h4>Recent reminders</h4>
          {messages.length === 0 ? <p className="management-empty">No reminders scheduled yet.</p> : messages.slice(0, 8).map((message) => <div key={message.id}><span><strong>{message.title}</strong><small>{formatScheduledDate(message.scheduled_for)} · {message.audience_type.replaceAll('_', ' ')}</small></span><em className={`message-status ${message.status}`}>{message.status}</em></div>)}
        </div>
      </article>

      <article className="management-card management-media-admin">
        <div className="management-card-heading"><div><h3>Gallery administration</h3><p>Select up to 100 visible media items and remove them through the same canonical deletion RPC used by mobile.</p></div><button className="danger-button" type="button" onClick={() => void deleteSelectedMedia()} disabled={busy !== null || selectedMediaIds.length === 0}>{busy === 'delete-media' ? 'Removing…' : `Remove selected (${selectedMediaIds.length})`}</button></div>
        <div className="management-media-grid">
          {media.map((item) => {
            const thumb = mediaThumb(item);
            const selected = selectedMediaIds.includes(item.id);
            return <button key={item.id} type="button" className={selected ? 'selected' : ''} onClick={() => toggleMedia(item.id)}><span className="management-media-check">{selected ? '✓' : ''}</span>{thumb ? <img src={thumb} alt="Event media" loading="lazy" /> : <span className="management-media-placeholder">{item.media_type === 'video' ? '▶' : 'Photo'}</span>}</button>;
          })}
        </div>
      </article>
    </section>
  );
}
